import React from 'react';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { useEmailAllCategoryTemplates, useEmailCategoryTemplates, useEmailSearchTemplates } from '../../../queries';
import { Industry, KnownCategories } from '../../../viewmodels/AppViewModels';
import { useCampaignParams } from '../CampaignBrowser';

export const useEmailCampaignBrowserData = (accountIndustry?: Industry) => {
	const { logApiError } = useEventLogging('EmailCampaignCards');
	const { category, industry, search, setCampaignBrowserParam } = useCampaignParams();
	const userSession = useUserSession();
	const allCategoryTemplatesQuery = useEmailAllCategoryTemplates({
		enabled: category === KnownCategories.All,
		industry,
		meta: {
			errorLogging: {
				category: 'useEmailCampaignBrowserData',
				action: 'LoadAllEmailCategoryTemplates-Error',
			},
		},
	});
	const featuredQuery = useEmailCategoryTemplates({
		categoryName: KnownCategories.Featured,
		enabled: true,
		industry: accountIndustry || industry || userSession?.account?.additionalInfo?.industry,
		meta: {
			errorLogging: {
				category: 'useEmailCampaignBrowserData',
				action: 'LoadSocialFeaturedTemplates-Error',
			},
		},
	});
	const categoryTemplatesQuery = useEmailCategoryTemplates({
		categoryName: category,
		enabled:
			industry != null &&
			category != null &&
			![KnownCategories.HtmlNewsletters, KnownCategories.All, KnownCategories.MyTemplates].includes(category),
		industry,
		// We don't want to retry if the category doesn't exist
		retry: (_, error) => error.systemCode !== 404,
	});
	React.useEffect(() => {
		if (!categoryTemplatesQuery.error) return;

		if (categoryTemplatesQuery.error?.systemCode === 404) {
			setCampaignBrowserParam({ category: KnownCategories.All });
		} else {
			logApiError('LoadEmailCategoryTemplates-Error', categoryTemplatesQuery.error);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [categoryTemplatesQuery.error]);
	const searchTemplatesQuery = useEmailSearchTemplates({
		enabled: !!search,
		industry,
		meta: {
			errorLogging: {
				category: 'useEmailCampaignBrowserData',
				action: 'SearchEmailCampaign-Error',
			},
		},
		query: search,
	});
	return {
		allCategoryTemplatesQuery,
		categoryTemplatesQuery,
		featuredQuery,
		searchTemplatesQuery,
	};
};
